import React, { useEffect, useState } from "react";

import { Button, Checkbox, Modal, Radio } from "antd";
import { toast } from "react-toastify";

import StyledStepper from "../../../../Common/UIComponents/StyledStepper";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import SampleSubmissionConfirmModal from "../../../SampleSubmissionModal/SampleSubmissionConfirmModal";

import { filterCompositeIdentifiers, isDropdownSampleField } from "../../../utils";

import { ReactComponent as QuestionIcon } from "../../../../../assets/images/question-mark-pop-up-filled.svg";

import "./CompositeModal.css";

function getSampleFieldDisplayTitle(sampleFieldsInfo, jsonField) {
  const raw = sampleFieldsInfo?.[jsonField]?.title_field;
  return typeof raw === "string" ? raw.trim() : "";
}

function getCompositeUniqueRadioValue(unique) {
  if (unique === true) return "yes";
  if (unique === false) return "no";
  return undefined;
}

export default function CompositeModal({
  showCompositeModal, setShowCompositeModal, sampleFieldsInfo, sampleFields = [], compositeData, setCompositeData, setCompositeToggle, handleCompositeSave,
}) {
  const [composite, setComposite] = useState({ sample_count: null, unique: null, identifier: [] });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  useEffect(() => {
    if (compositeData) {
      setComposite({
        ...compositeData,
        identifier: filterCompositeIdentifiers(compositeData.identifier, sampleFieldsInfo),
      });
    }
  }, [compositeData, sampleFieldsInfo]);

  /**
   * Handle changes to the composite sample input fields.
   * @param {Object} value The new value of the input field.
   * @param {string} action The name of the input field that changed.
   */
  const handleCompositeChange = (value, action) => {
    setComposite((prev) => {
      switch (action) {
        case "sample_count":
          return { ...prev, sample_count: value };
        case "unique":
          // If unique is set to false, reset the identifier array
          if (value.target.value === false) {
            return { ...prev, unique: value.target.value, identifier: [] };
          }
          return { ...prev, unique: value.target.value };
        case "identifier":
          return {
            ...prev,
            identifier: prev.identifier.includes(value)
              ? prev.identifier.filter((item) => item !== value)
              : [...prev.identifier, value],
          };
        default:
          return prev;
      }
    });
  };

  const handleSubmit = () => {
    const identifier = filterCompositeIdentifiers(composite.identifier, sampleFieldsInfo);
    const compositeToSave = { ...composite, identifier };

    if (compositeToSave.sample_count < 2) {
      toast.error("Composite sample must include at least 2 samples");
      return;
    }
    if (compositeToSave.unique === null) {
      toast.error("Please select whether all samples are unique or not");
      return;
    }
    if (compositeToSave.unique === true && compositeToSave.identifier.length === 0) {
      toast.error("Please select at least one identifier");
      return;
    }
    if (compositeToSave.sample_count < compositeData.sample_count && !showConfirmationModal) {
      setShowConfirmationModal(true); // Open confirmation modal
      const identifierTitles = compositeData.identifier.map((id) => getSampleFieldDisplayTitle(sampleFieldsInfo, id));
      setConfirmMessage(
        `This action will result in the loss of data for the last ${compositeData.sample_count - compositeToSave.sample_count} ${identifierTitles.join(", ")} field(s). Do you wish to continue?`,
      );
    } else {
      setCompositeData(compositeToSave);
      setCompositeToggle(true);
      setShowCompositeModal(false);
      setShowConfirmationModal(false);
      handleCompositeSave(compositeToSave.sample_count, compositeToSave.identifier);
    }
  };

  const handleClose = () => {
    if (compositeData) {
      setComposite({
        ...compositeData,
        identifier: filterCompositeIdentifiers(compositeData.identifier, sampleFieldsInfo),
      });
    }
    setShowCompositeModal(false);
  };

  return (
    <>
      <Modal
        open={showCompositeModal}
        onCancel={handleClose}
        centered
        footer={null}
        destroyOnClose
        width="540px"
      >
        <div className="CompositeModal__Container">
          <div className="CompositeModal__Header">
            <span>Composite Sample</span>
          </div>
          <div className="CompositeModal__Body">
            <div className="CompositeModal__InputContainer">
              <div className="CompositeModal__InputQuestion__Container">
                <span className="CompositeModal__InputQuestion">
                  How many samples do you have in this composite?
                </span>
                <StyledTooltip title="A composite sample must include at least 2 samples" placement="top">
                  <QuestionIcon width={20} height={20} />
                </StyledTooltip>
              </div>
              <div className="CompositeModal--MarginLeft">
                <StyledStepper
                  value={composite.sample_count}
                  onChange={(value) => handleCompositeChange(value, "sample_count")}
                  max={150}
                />
              </div>
            </div>
            <div className="CompositeModal__InputContainer">
              <span className="CompositeModal__InputQuestion">
                Is there any unique identifier(s) for each sample in the composite?
              </span>
              <div className="CompositeModal--MarginLeft">
                <Radio.Group
                  className="CompositeModal__RadioGroup"
                  name="composite-unique-identifier"
                  value={getCompositeUniqueRadioValue(composite.unique)}
                >
                  <div
                    className={`CompositeModal__RadioOption${composite.unique === true ? " CompositeModal__RadioOption--checked" : ""}`}
                    onClick={() => handleCompositeChange({ target: { value: true } }, "unique")}
                  >
                    <Radio value="yes">Yes</Radio>
                  </div>
                  <div
                    className={`CompositeModal__RadioOption${composite.unique === false ? " CompositeModal__RadioOption--checked" : ""}`}
                    onClick={() => handleCompositeChange({ target: { value: false } }, "unique")}
                  >
                    <Radio value="no">No</Radio>
                  </div>
                </Radio.Group>
              </div>
            </div>
            {composite.unique && (
              <div className="CompositeModal__SelectionContainer">
                <span className="CompositeModal__SelectionInputQuestion">Select the unique identifier(s):</span>
                <div className="CompositeModal__SelectionInputContainer CompositeModal--MarginLeft">
                  {(sampleFields.length ? sampleFields : Object.keys(sampleFieldsInfo)).map((field, idx) => {
                    const info = sampleFieldsInfo[field];
                    if (!info || info.display !== "1" || isDropdownSampleField(info)) {
                      return null;
                    }
                    return (
                      <div key={`${idx}--${field}`} className="CompositeModal__SelectionInput">
                        <Checkbox
                          checked={composite.identifier.includes(field)}
                          onChange={() => handleCompositeChange(field, "identifier")}
                        >
                          <span>{getSampleFieldDisplayTitle(sampleFieldsInfo, field)}</span>
                        </Checkbox>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="CompositeModal__Footer">
            <Button
              type="default"
              className="CompositeModal__Footer__Button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="CompositeModal__Footer__Button"
              onClick={handleSubmit}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
      {showConfirmationModal && (
        <SampleSubmissionConfirmModal
          headerText="Unsaved Changes"
          bodyText={confirmMessage}
          buttonText={["No", "Yes"]}
          buttonFunctions={[() => { setShowConfirmationModal(false); }, () => handleSubmit()]}
        />
      )}
    </>
  );
}
